/**
 * Knowledge Base Module - Full Page View with Two Tabs
 * Tab 1: Simple Facts (existing knowledge entries with CRUD)
 * Tab 2: COGITO Graph (symbolic knowledge from voice conversations)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient, {
  getCogitoGraph,
  getCogitoMermaid,
  deleteCogitoEntity,
  deleteCogitoRelationship,
  resetCogitoGraph,
  CogitoEntity,
  CogitoRelationship,
  CogitoInsight,
  ensureKbFolder,
  listKbFiles,
  upgradeKbFile,
  copyDiskFileToKb,
  deleteKbFile,
} from '@/services/api';
import { uploadFileWithProgress } from '@/services/filesApi';
import { getFileTypeConfig, formatFileSize } from '@/types/files';
import type { FileNode } from '@/types/files';
import FileProcessingBadge from '@/components/files/FileProcessingBadge';
import DiskFilePickerModal from '@/components/tasks/DiskFilePickerModal';
import { useModuleRefresh } from '@/contexts/ModuleRefreshContext';
import MermaidDiagram from '@/components/MermaidDiagram';

// ============================================================================
// Interfaces
// ============================================================================

interface KnowledgeEntry {
  id: string;
  title: string;
  content: string;
  category: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
  metadata?: Record<string, unknown>;
}

type TabType = 'facts' | 'cogito';
type CogitoViewType = 'list' | 'diagram';

// ============================================================================
// Entity Type Colors and Icons
// ============================================================================

const ENTITY_TYPE_STYLES: Record<string, { bg: string; text: string; icon: string }> = {
  PERSON: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '👤' },
  ORGANIZATION: { bg: 'bg-purple-100', text: 'text-purple-700', icon: '🏢' },
  PLACE: { bg: 'bg-green-100', text: 'text-green-700', icon: '📍' },
  CONCEPT: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: '💡' },
  EVENT: { bg: 'bg-red-100', text: 'text-red-700', icon: '📅' },
  THING: { bg: 'bg-gray-100', text: 'text-gray-700', icon: '📦' },
  TIME: { bg: 'bg-orange-100', text: 'text-orange-700', icon: '⏰' },
};

const INSIGHT_CATEGORY_STYLES: Record<string, { bg: string; text: string; icon: string }> = {
  preference: { bg: 'bg-pink-100', text: 'text-pink-700', icon: '❤️' },
  goal: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: '🎯' },
  fact: { bg: 'bg-blue-100', text: 'text-blue-700', icon: '📋' },
  opinion: { bg: 'bg-amber-100', text: 'text-amber-700', icon: '💭' },
  question: { bg: 'bg-violet-100', text: 'text-violet-700', icon: '❓' },
  pattern: { bg: 'bg-cyan-100', text: 'text-cyan-700', icon: '🔄' },
};

// ============================================================================
// Main Component
// ============================================================================

const KnowledgeBaseModule: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { registerKnowledgeRefresh } = useModuleRefresh();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('facts');

  // Simple Facts state
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeEntry | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);

  // Form state for Simple Facts
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: 'general'
  });

  // COGITO state
  const [cogitoView, setCogitoView] = useState<CogitoViewType>('list');
  const [cogitoEntities, setCogitoEntities] = useState<CogitoEntity[]>([]);
  const [cogitoRelationships, setCogitoRelationships] = useState<CogitoRelationship[]>([]);
  const [cogitoInsights, setCogitoInsights] = useState<CogitoInsight[]>([]);
  const [cogitoMermaid, setCogitoMermaid] = useState<string>('');
  const [cogitoLoading, setCogitoLoading] = useState(false);
  const [cogitoSearchQuery, setCogitoSearchQuery] = useState('');
  const [cogitoTypeFilter, setCogitoTypeFilter] = useState<string>('all');

  // ============================================================================
  // Simple Facts Logic
  // ============================================================================

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/personal-data/knowledge');
      setEntries(response.data);
    } catch (error) {
      console.error('Failed to load knowledge:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  useEffect(() => {
    registerKnowledgeRefresh(loadEntries);
  }, [registerKnowledgeRefresh, loadEntries]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/personal-data/knowledge', formData);
      await loadEntries();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create entry:', error);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEntry) return;

    try {
      await apiClient.put(`/personal-data/knowledge/${selectedEntry.id}`, formData);
      await loadEntries();
      setShowEditModal(false);
      setSelectedEntry(null);
      resetForm();
    } catch (error) {
      console.error('Failed to update entry:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('knowledge.cogito.deleteKnowledgeConfirm'))) return;

    try {
      await apiClient.delete(`/personal-data/knowledge/${id}`);
      await loadEntries();
      setSelectedEntry(null);
    } catch (error) {
      console.error('Failed to delete entry:', error);
    }
  };

  const openEditModal = (entry: KnowledgeEntry) => {
    setSelectedEntry(entry);
    setFormData({
      title: entry.title || '',
      content: entry.content || '',
      category: entry.category || 'general'
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      category: 'general'
    });
  };

  const filteredEntries = entries.filter(entry => {
    const title = entry.title || '';
    const content = entry.content || '';
    const category = entry.category || '';
    const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', ...Array.from(new Set(entries.map(e => e.category || 'general').filter(Boolean)))];

  // ============================================================================
  // COGITO Logic
  // ============================================================================

  const loadCogitoData = useCallback(async () => {
    setCogitoLoading(true);
    try {
      const [graphResponse, mermaidResponse] = await Promise.all([
        getCogitoGraph(),
        getCogitoMermaid()
      ]);

      setCogitoEntities(graphResponse.entities);
      setCogitoRelationships(graphResponse.relationships);
      setCogitoInsights(graphResponse.insights);
      setCogitoMermaid(mermaidResponse.mermaid);
    } catch (error) {
      console.error('Failed to load COGITO data:', error);
    } finally {
      setCogitoLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'cogito') {
      loadCogitoData();
    }
  }, [activeTab, loadCogitoData]);

  const handleDeleteEntity = async (entityId: string) => {
    if (!confirm(t('knowledge.cogito.deleteEntityConfirm'))) return;
    try {
      await deleteCogitoEntity(entityId);
      await loadCogitoData();
    } catch (error) {
      console.error('Failed to delete entity:', error);
    }
  };

  const handleDeleteRelationship = async (relationshipId: string) => {
    if (!confirm(t('knowledge.cogito.deleteRelationshipConfirm'))) return;
    try {
      await deleteCogitoRelationship(relationshipId);
      await loadCogitoData();
    } catch (error) {
      console.error('Failed to delete relationship:', error);
    }
  };

  const handleResetGraph = async () => {
    if (!confirm(t('knowledge.cogito.resetGraphConfirm'))) return;
    try {
      await resetCogitoGraph();
      await loadCogitoData();
    } catch (error) {
      console.error('Failed to reset graph:', error);
    }
  };

  // Filter COGITO entities
  const filteredCogitoEntities = cogitoEntities.filter(entity => {
    const matchesSearch = entity.name.toLowerCase().includes(cogitoSearchQuery.toLowerCase()) ||
                         entity.description?.toLowerCase().includes(cogitoSearchQuery.toLowerCase());
    const matchesType = cogitoTypeFilter === 'all' || entity.entity_type === cogitoTypeFilter;
    return matchesSearch && matchesType;
  });

  const entityTypes = ['all', ...Array.from(new Set(cogitoEntities.map(e => e.entity_type)))];

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="bg-gray-50" style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}>
      {/* Header - uses CSS variable from HealthBanner */}
      <div className="bg-white border-b border-gray-200 sticky z-30" style={{ top: 'var(--health-banner-height, 0px)' }}>
        <div className="px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/voice')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900">{t('knowledge.title')}</h1>
                <p className="text-xs md:text-sm text-gray-500">
                  {activeTab === 'facts'
                    ? t('knowledge.entries', { count: entries.length })
                    : t('knowledge.entitiesAndRelationships', { entities: cogitoEntities.length, relationships: cogitoRelationships.length })}
                </p>
              </div>
            </div>

            {activeTab === 'facts' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-primary text-sm px-3 md:px-4 py-2 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">{t('knowledge.newEntry')}</span>
                <span className="sm:hidden">{t('knowledge.new')}</span>
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="mt-4 flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('facts')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'facts'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('knowledge.tabs.simpleFacts')}
            </button>
            <button
              onClick={() => setActiveTab('cogito')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'cogito'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('knowledge.tabs.cogitoGraph')}
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'facts' ? (
        <SimpleFactsTab
          loading={loading}
          filteredEntries={filteredEntries}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          categories={categories}
          openEditModal={openEditModal}
          handleDelete={handleDelete}
          setShowCreateModal={setShowCreateModal}
        />
      ) : (
        <CogitoGraphTab
          loading={cogitoLoading}
          entities={filteredCogitoEntities}
          relationships={cogitoRelationships}
          insights={cogitoInsights}
          mermaid={cogitoMermaid}
          view={cogitoView}
          setView={setCogitoView}
          searchQuery={cogitoSearchQuery}
          setSearchQuery={setCogitoSearchQuery}
          typeFilter={cogitoTypeFilter}
          setTypeFilter={setCogitoTypeFilter}
          entityTypes={entityTypes}
          onDeleteEntity={handleDeleteEntity}
          onDeleteRelationship={handleDeleteRelationship}
          onResetGraph={handleResetGraph}
          onRefresh={loadCogitoData}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Modal title={t('knowledge.form.createEntry')} onClose={() => { setShowCreateModal(false); resetForm(); }}>
          <EntryForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreate}
            onCancel={() => { setShowCreateModal(false); resetForm(); }}
            submitLabel={t('knowledge.facts.createEntry')}
          />
        </Modal>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedEntry && (
        <Modal title={t('knowledge.form.editEntry')} onClose={() => { setShowEditModal(false); setSelectedEntry(null); resetForm(); }}>
          <EntryForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleUpdate}
            onCancel={() => { setShowEditModal(false); setSelectedEntry(null); resetForm(); }}
            submitLabel={t('knowledge.form.saveChanges')}
          />
          <KbFilesSection kbId={selectedEntry.id} />
        </Modal>
      )}
    </div>
  );
};

// ============================================================================
// Simple Facts Tab Component
// ============================================================================

interface SimpleFactsTabProps {
  loading: boolean;
  filteredEntries: KnowledgeEntry[];
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  categoryFilter: string;
  setCategoryFilter: (c: string) => void;
  categories: string[];
  openEditModal: (entry: KnowledgeEntry) => void;
  handleDelete: (id: string) => void;
  setShowCreateModal: (show: boolean) => void;
}

const SimpleFactsTab: React.FC<SimpleFactsTabProps> = ({
  loading,
  filteredEntries,
  searchQuery,
  setSearchQuery,
  categoryFilter,
  setCategoryFilter,
  categories,
  openEditModal,
  handleDelete,
  setShowCreateModal
}) => {
  const { t } = useTranslation('common');
  return (
    <>
      {/* Search and Filters */}
      <div className="px-4 md:px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 max-w-7xl mx-auto">
          <div className="flex-1 relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('knowledge.search.searchKnowledge')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat === 'all' ? t('knowledge.search.allCategories') : cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('knowledge.facts.noEntries')}</h3>
            <p className="text-gray-500 mb-4">{t('knowledge.facts.startBuilding')}</p>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary">
              {t('knowledge.facts.createEntry')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {filteredEntries.map((entry) => (
              <div
                key={entry.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => openEditModal(entry)}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 flex-1 pr-2">{entry.title || t('knowledge.facts.untitled')}</h3>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                      {entry.category || 'general'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3">{entry.content || ''}</p>
                  <div className="text-xs text-gray-500">
                    {t('knowledge.facts.updated')} {entry.updated_at ? new Date(entry.updated_at).toLocaleDateString() : t('knowledge.facts.unknown')}
                  </div>
                </div>
                <div className="border-t border-gray-100 px-4 py-2 flex items-center justify-between">
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditModal(entry); }}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    {t('buttons.edit')}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }}
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    {t('buttons.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

// ============================================================================
// COGITO Graph Tab Component
// ============================================================================

interface CogitoGraphTabProps {
  loading: boolean;
  entities: CogitoEntity[];
  relationships: CogitoRelationship[];
  insights: CogitoInsight[];
  mermaid: string;
  view: CogitoViewType;
  setView: (v: CogitoViewType) => void;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  typeFilter: string;
  setTypeFilter: (t: string) => void;
  entityTypes: string[];
  onDeleteEntity: (id: string) => void;
  onDeleteRelationship: (id: string) => void;
  onResetGraph: () => void;
  onRefresh: () => void;
}

const CogitoGraphTab: React.FC<CogitoGraphTabProps> = ({
  loading,
  entities,
  relationships,
  insights,
  mermaid,
  view,
  setView,
  searchQuery,
  setSearchQuery,
  typeFilter,
  setTypeFilter,
  entityTypes,
  onDeleteEntity,
  onDeleteRelationship,
  onResetGraph,
  onRefresh
}) => {
  const { t } = useTranslation('common');
  return (
    <>
      {/* Toolbar */}
      <div className="px-4 md:px-6 py-4 bg-white border-b border-gray-200">
        <div className="flex flex-col sm:flex-row gap-3 max-w-7xl mx-auto">
          {/* View Toggle */}
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              onClick={() => setView('list')}
              className={`px-4 py-2 text-sm font-medium ${
                view === 'list' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t('knowledge.cogito.listView')}
            </button>
            <button
              onClick={() => setView('diagram')}
              className={`px-4 py-2 text-sm font-medium ${
                view === 'diagram' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {t('knowledge.cogito.diagramView')}
            </button>
          </div>

          {view === 'list' && (
            <>
              <div className="flex-1 relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('knowledge.search.searchEntities')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                {entityTypes.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? t('knowledge.search.allTypes') : type}
                  </option>
                ))}
              </select>
            </>
          )}

          <div className="flex gap-2 ml-auto">
            <button
              onClick={onRefresh}
              className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title={t('buttons.refresh')}
            >
              🔄
            </button>
            <button
              onClick={onResetGraph}
              className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title={t('knowledge.cogito.clear')}
            >
              {t('knowledge.cogito.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-6 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
          </div>
        ) : view === 'list' ? (
          <CogitoListView
            entities={entities}
            relationships={relationships}
            insights={insights}
            onDeleteEntity={onDeleteEntity}
            onDeleteRelationship={onDeleteRelationship}
          />
        ) : (
          <CogitoDiagramView mermaid={mermaid} />
        )}
      </div>
    </>
  );
};

// ============================================================================
// COGITO List View Component
// ============================================================================

interface CogitoListViewProps {
  entities: CogitoEntity[];
  relationships: CogitoRelationship[];
  insights: CogitoInsight[];
  onDeleteEntity: (id: string) => void;
  onDeleteRelationship: (id: string) => void;
}

const CogitoListView: React.FC<CogitoListViewProps> = ({
  entities,
  relationships,
  insights,
  onDeleteEntity,
  onDeleteRelationship
}) => {
  const { t } = useTranslation('common');
  const [expandedSection, setExpandedSection] = useState<'entities' | 'relationships' | 'insights'>('entities');

  if (entities.length === 0 && relationships.length === 0 && insights.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <div className="text-6xl mb-4">🧠</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('knowledge.cogito.noKnowledge')}</h3>
        <p className="text-gray-500 mb-4">
          {t('knowledge.cogito.noKnowledgeDescription')}
          <br />
          {t('knowledge.cogito.noKnowledgeExtra')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Entities Section */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'entities' ? 'entities' : 'entities')}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 border-b border-gray-200"
        >
          <span className="font-semibold text-gray-900">
            {t('knowledge.cogito.entities')} ({entities.length})
          </span>
          <svg className={`w-5 h-5 text-gray-500 transition-transform ${expandedSection === 'entities' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {expandedSection === 'entities' && (
          <div className="divide-y divide-gray-100">
            {entities.map(entity => {
              const style = ENTITY_TYPE_STYLES[entity.entity_type] || ENTITY_TYPE_STYLES.THING;
              // Handle both string (legacy) and object (current) attributes
              const attrs = typeof entity.attributes === 'string'
                ? JSON.parse(entity.attributes || '{}')
                : (entity.attributes || {});
              return (
                <div key={entity.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{style.icon}</span>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium text-gray-900">{entity.name}</h4>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${style.bg} ${style.text}`}>
                            {entity.entity_type}
                          </span>
                          <span className="text-xs text-gray-400">
                            {t('knowledge.cogito.confidence', { percent: Math.round(entity.confidence * 100) })}
                          </span>
                        </div>
                        {entity.description && (
                          <p className="text-sm text-gray-600 mt-1">{entity.description}</p>
                        )}
                        {Object.keys(attrs).length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {Object.entries(attrs).map(([key, value]) => (
                              <span key={key} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                                {key}: {String(value)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => onDeleteEntity(entity.id)}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      {t('buttons.delete')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Relationships Section */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'relationships' ? 'entities' : 'relationships')}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 border-b border-gray-200"
        >
          <span className="font-semibold text-gray-900">
            {t('knowledge.cogito.relationships')} ({relationships.length})
          </span>
          <svg className={`w-5 h-5 text-gray-500 transition-transform ${expandedSection === 'relationships' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {expandedSection === 'relationships' && (
          <div className="divide-y divide-gray-100">
            {relationships.length === 0 ? (
              <div className="p-4 text-center text-gray-500">{t('knowledge.cogito.noRelationships')}</div>
            ) : (
              relationships.map(rel => (
                <div key={rel.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-gray-900">{rel.source_name}</span>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                        {rel.relationship_type.replace(/_/g, ' ')}
                      </span>
                      <span className="font-medium text-gray-900">{rel.target_name}</span>
                      <span className="text-xs text-gray-400 ml-2">
                        {Math.round(rel.confidence * 100)}%
                      </span>
                    </div>
                    <button
                      onClick={() => onDeleteRelationship(rel.id)}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      {t('buttons.delete')}
                    </button>
                  </div>
                  {rel.description && (
                    <p className="text-xs text-gray-500 mt-1 ml-4">{rel.description}</p>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Insights Section */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'insights' ? 'entities' : 'insights')}
          className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 border-b border-gray-200"
        >
          <span className="font-semibold text-gray-900">
            {t('knowledge.cogito.insights')} ({insights.length})
          </span>
          <svg className={`w-5 h-5 text-gray-500 transition-transform ${expandedSection === 'insights' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {expandedSection === 'insights' && (
          <div className="divide-y divide-gray-100">
            {insights.length === 0 ? (
              <div className="p-4 text-center text-gray-500">{t('knowledge.cogito.noInsights')}</div>
            ) : (
              insights.map(insight => {
                const style = INSIGHT_CATEGORY_STYLES[insight.category] || INSIGHT_CATEGORY_STYLES.fact;
                return (
                  <div key={insight.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-3">
                      <span className="text-xl">{style.icon}</span>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 text-xs rounded-full ${style.bg} ${style.text}`}>
                            {insight.category}
                          </span>
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            insight.importance === 'high' ? 'bg-red-100 text-red-700' :
                            insight.importance === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {insight.importance}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900">{insight.content}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(insight.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// COGITO Diagram View Component
// ============================================================================

interface CogitoDiagramViewProps {
  mermaid: string;
}

const CogitoDiagramView: React.FC<CogitoDiagramViewProps> = ({ mermaid }) => {
  const { t } = useTranslation('common');
  const [showCode, setShowCode] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopyCode = () => {
    navigator.clipboard.writeText(mermaid);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  if (!mermaid || mermaid === 'graph TD') {
    return (
      <div className="bg-white rounded-lg shadow-sm p-12 text-center">
        <div className="text-6xl mb-4">🔗</div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('knowledge.cogito.noGraph')}</h3>
        <p className="text-gray-500">
          {t('knowledge.cogito.noGraphDescription')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Diagram Card */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
            </svg>
            {t('knowledge.cogito.knowledgeGraph')}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCode(!showCode)}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                showCode
                  ? 'bg-gray-700 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {showCode ? t('knowledge.cogito.hideCode') : t('knowledge.cogito.showCode')}
            </button>
            <button
              onClick={handleCopyCode}
              className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1"
            >
              {copySuccess ? (
                <>
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-green-600">{t('knowledge.cogito.copied')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {t('knowledge.cogito.copyCode')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Visual Diagram */}
        <div className="p-4">
          <MermaidDiagram chart={mermaid} className="w-full" />
        </div>
      </div>

      {/* Code Panel (collapsible) */}
      {showCode && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
            <h4 className="font-medium text-gray-700 text-sm">{t('knowledge.cogito.mermaidSource')}</h4>
          </div>
          <div className="bg-gray-900 p-4 overflow-x-auto">
            <pre className="text-sm text-green-400 font-mono whitespace-pre">{mermaid}</pre>
          </div>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <p className="text-xs text-gray-500">
              {t('knowledge.cogito.mermaidLiveHint')}{' '}
              <a href="https://mermaid.live" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                mermaid.live
              </a>{' '}
              {t('knowledge.cogito.mermaidLiveLink')}
            </p>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {t('knowledge.cogito.legend')}
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          <div className="flex items-center gap-2 text-xs">
            <span className="w-8 h-6 border-2 border-blue-500 rounded flex items-center justify-center bg-blue-50">A</span>
            <span className="text-gray-600">{t('knowledge.cogito.legendPersonThing')}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-8 h-6 border-2 border-purple-500 rounded-full flex items-center justify-center bg-purple-50">B</span>
            <span className="text-gray-600">{t('knowledge.cogito.legendOrganization')}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-8 h-6 border-2 border-green-500 flex items-center justify-center bg-green-50" style={{ clipPath: 'polygon(15% 0%, 85% 0%, 100% 100%, 0% 100%)' }}>C</span>
            <span className="text-gray-600">{t('knowledge.cogito.legendPlace')}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-8 h-6 border-2 border-yellow-500 flex items-center justify-center bg-yellow-50" style={{ clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' }}>D</span>
            <span className="text-gray-600">{t('knowledge.cogito.legendConcept')}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="w-8 h-6 border-2 border-orange-500 rounded-lg flex items-center justify-center bg-orange-50">E</span>
            <span className="text-gray-600">{t('knowledge.cogito.legendEventTime')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Knowledge Base Files Section
// ============================================================================

interface KbFilesSectionProps {
  kbId: string;
}

const KbFilesSection: React.FC<KbFilesSectionProps> = ({ kbId }) => {
  const { t } = useTranslation('common');
  const [files, setFiles] = useState<FileNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDiskPicker, setShowDiskPicker] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFiles = useCallback(async () => {
    setLoading(true);
    try {
      const result = await listKbFiles(kbId);
      setFiles(result);
      setError(null);
    } catch (err) {
      console.error('Failed to load KB files:', err);
      setError(err instanceof Error ? err.message : 'Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [kbId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleFileUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    try {
      const { folder_id } = await ensureKbFolder(kbId);
      const result = await uploadFileWithProgress(file, folder_id, (progress) => {
        setUploadProgress(progress.percentage);
      });
      if (result?.file?.id) {
        await upgradeKbFile(kbId, result.file.id);
      }
      await loadFiles();
    } catch (err) {
      console.error('Failed to upload KB file:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDiskFilesCopied = async (copiedFiles: { id: string; name: string }[]) => {
    setShowDiskPicker(false);
    setError(null);
    const errors: string[] = [];
    for (const file of copiedFiles) {
      try {
        await copyDiskFileToKb(kbId, file.id);
      } catch (err) {
        console.error(`Failed to copy file ${file.name}:`, err);
        errors.push(file.name);
      }
    }
    if (errors.length > 0) {
      setError(`Failed to attach: ${errors.join(', ')}`);
    }
    await loadFiles();
  };

  const handleDeleteFile = async (fileId: string, fileName: string) => {
    if (!confirm(`Delete "${fileName}"?`)) return;
    try {
      await deleteKbFile(kbId, fileId);
      await loadFiles();
    } catch (err) {
      console.error('Failed to delete KB file:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete file');
    }
  };

  return (
    <div className="border-t border-gray-200 px-6 py-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">
        {t('knowledge.files.title', 'Files')} {files.length > 0 && `(${files.length})`}
      </h3>

      {error && (
        <div className="mb-2 px-3 py-1.5 text-xs text-red-700 bg-red-50 rounded border border-red-200 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600 ml-2">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* File list */}
      {loading ? (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
        </div>
      ) : files.length > 0 ? (
        <div className="space-y-2 mb-3">
          {files.map((file) => {
            const config = getFileTypeConfig(file);
            return (
              <div key={file.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 group">
                <span className="text-base flex-shrink-0">{config.icon}</span>
                <span className="text-sm text-gray-800 truncate flex-1" title={file.name}>{file.name}</span>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {formatFileSize(file.file_size ?? null)}
                </span>
                <FileProcessingBadge
                  fileId={file.id}
                  contentType={file.content_type ?? null}
                  compact
                />
                <button
                  onClick={() => handleDeleteFile(file.id, file.name)}
                  className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  title={t('buttons.delete', 'Delete')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Upload area */}
      {uploading ? (
        <div className="py-3">
          <div className="flex items-center gap-2 text-sm text-blue-600 mb-1">
            <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
            <span>{t('knowledge.files.uploading', 'Uploading...')} {uploadProgress}%</span>
          </div>
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <p className="text-xs text-gray-500 mb-2">
            {t('knowledge.files.dropHere', 'Drop file here or use buttons below')}
          </p>
          <div className="flex gap-2 justify-center">
            <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              {t('knowledge.files.upload', 'Upload File')}
              <input type="file" className="hidden" onChange={handleInputChange} />
            </label>
            <button
              onClick={() => setShowDiskPicker(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded-md hover:bg-emerald-100 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
              {t('knowledge.files.attachFromDisk', 'From Disk')}
            </button>
          </div>
        </div>
      )}

      {/* Disk file picker modal */}
      <DiskFilePickerModal
        isOpen={showDiskPicker}
        onClose={() => setShowDiskPicker(false)}
        onFilesCopied={handleDiskFilesCopied}
      />
    </div>
  );
};

// ============================================================================
// Modal Component
// ============================================================================

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold">{title}</h2>
        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {children}
    </div>
  </div>
);

// ============================================================================
// Entry Form Component
// ============================================================================

interface EntryFormProps {
  formData: { title: string; content: string; category: string };
  setFormData: (data: { title: string; content: string; category: string }) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  submitLabel: string;
}

const EntryForm: React.FC<EntryFormProps> = ({ formData, setFormData, onSubmit, onCancel, submitLabel }) => {
  const { t } = useTranslation('common');
  return (
    <form onSubmit={onSubmit} className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('knowledge.form.title')}</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('knowledge.form.titlePlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('knowledge.form.content')}</label>
        <textarea
          value={formData.content}
          onChange={(e) => setFormData({ ...formData, content: e.target.value })}
          required
          rows={8}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder={t('knowledge.form.contentPlaceholder')}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('knowledge.form.category')}</label>
        <select
          value={formData.category}
          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="general">{t('knowledge.form.categories.general')}</option>
          <option value="technical">{t('knowledge.form.categories.technical')}</option>
          <option value="business">{t('knowledge.form.categories.business')}</option>
          <option value="personal">{t('knowledge.form.categories.personal')}</option>
          <option value="reference">{t('knowledge.form.categories.reference')}</option>
        </select>
      </div>

      <div className="flex gap-3 pt-4">
        <button type="submit" className="btn-primary flex-1">
          {submitLabel}
        </button>
        <button type="button" onClick={onCancel} className="btn-secondary flex-1">
          {t('buttons.cancel')}
        </button>
      </div>
    </form>
  );
};

export default KnowledgeBaseModule;
